import React, {useEffect, useState} from 'react';
import {Box, Button, Divider, Drawer, IconButton, InputLabel, Stack, Typography} from '@mui/material';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {finalize, map} from 'rxjs';

import {ClientsContract, clientsContractsService, ManageClientContract} from '@store/clients/contracts';
import {clientsUnitsService} from '@store/clients/units';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import {Close} from '@icons/index';
import {Sizes} from '@constants/sizes.constants';

import TextFieldComponent from '@components/inputs/textField/TextField.component';
import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import DatePickerComponent from '@components/inputs/datePicker/DatePicker.component';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContractServicesModal
  from '@screens/admin/clients/details/contractedServices/manageService/steps/ContractServices.modal';
import {clientDetailsService} from '@store/clients/details';
import AsyncSelectComponent from '@components/inputs/asyncSelect/AsyncSelect.component';
import UpdateWasteContainerModal from '@screens/admin/clients/contracts/UpdateWasteContainer.modal';
import dayjs from 'dayjs';

interface ManageContractDrawerProps {
  handleClose?: () => void;
  handleCreated?: (contract?: ClientsContract) => void;
  isCreation?: boolean;
  manageContract?: ManageClientContract;
}

const ManageContractDrawer = (props: ManageContractDrawerProps) => {
  const {handleClose: handleCloseProps, handleCreated, isCreation, manageContract} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [contract, setContract] = useState<ManageClientContract>(manageContract ? manageContract : {startDate: new Date()});
  const [showContractServices, setShowContractServices] = useState<number | undefined>(undefined);
  const [showWasteContainer, setShowWasteContainer] = useState<boolean>(false);

  const {contractId} = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClose = () => handleCloseProps ? handleCloseProps() : navigate('..', {replace: true});

  const updateContract = (data: ManageClientContract) => setContract((state) => ({...state, ...data}));

  const handleSave = () => {
    if (isCreation) {
      if (!contract.clientUnit) return;
      setLoading(true);
      clientsContractsService.createClientContract(contract.clientUnit.value, contract).pipe(finalize(() => setLoading(false))).subscribe({
        next: (c) => {
          enqueueSnackbar(t('clients.details.contracts.manage.success.create'), {variant: 'success'});
          handleClose();
          handleCreated?.(c);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    } else if (manageContract?.id) {
      setLoading(true);
      clientsContractsService.updateClientContract(manageContract.id, contract).pipe(finalize(() => setLoading(false))).subscribe({
        next: (c) => {
          enqueueSnackbar(t('clients.details.contracts.manage.success.update'), {variant: 'success'});
          handleClose();
          handleCreated?.(c);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  };

  const handleDuplicate = () => {
    navigate({
      pathname: '/clients/services/add',
      search: `?type=DUPLICATE&object=${contract.id}`
    });
  };

  const errors = {
    contractName: getFieldError(contract.contractName, FieldValidationType.REQUIRED_STRING),
    clientUnit: getFieldError(contract.clientUnit, FieldValidationType.REQUIRED_SELECT_ITEM),
    startDate: getFieldError(contract.startDate, FieldValidationType.REQUIRED_DATE),
    endDate: getFieldError(contract.endDate, FieldValidationType.DATE),
  };

  const {invoiceAddress: address} = contract;

  useEffect(() => {
    if (contractId) {
      clientsContractsService.getContract(contractId).subscribe({
        next: (contract) => {
          setContract(ClientsContract.toManageClientsContract(contract));
          const endDate = searchParams.get('endDate');
          if (endDate && endDate != '') {
            clientsContractsService.updateClientContract(contractId, {
              ...contract,
              endDate: new Date(endDate)
            }).subscribe({
              next: (c) => clientsContractsService.getContract(contractId).subscribe({
                next: (contract) => setContract(ClientsContract.toManageClientsContract(contract))
              }),
              error: (err) => enqueueSnackbar(err.text, err.options),
            });
          }
        }
      });
    }
  }, [contractId, enqueueSnackbar, searchParams]);

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack overflow="hidden">
        <Stack direction="row" justifyContent="flex-end">
          <IconButton onClick={handleClose}>
            <Close/>
          </IconButton>
        </Stack>
        <Stack pr="10px">
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5} pb="8px">
            <Typography fontSize={24} fontWeight="700">
              {isCreation ? t('clients.details.contracts.manage.title.add') : ''}
            </Typography>
            <Stack direction="row" spacing={2}>
              {!isCreation && <Button
                  onClick={() => setShowWasteContainer(true)}
                  variant="outlined"
                  color="primary">
                {t('clients.details.contracts.updateWasteContainer')}</Button>
              }
              {!isCreation && <Button
                  onClick={handleDuplicate}
                  variant="outlined"
                  color="primary"
              >{t('global.duplicate')}</Button>}
            </Stack>

          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Stack pb="24px" pr="10px">
          <Stack py="20px" spacing={2}>
            <TextFieldComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('clients.details.contracts.manage.name')}
              placeholder={t('global.writeSomething')}
              handleChange={(contractName) => updateContract({contractName})}
              value={contract.contractName || ''}
              error={errors.contractName}
              required/>
            <AsyncSelectWithSearchComponent
              readOnly={!isCreation}
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('clients.details.contracts.manage.clientUnit')}
              placeholder={t('global.selectOption')}
              handleChange={(clientUnit) => updateContract({clientUnit})}
              value={contract.clientUnit}
              getOptions={(search) => clientsUnitsService.searchClientsUnits(search, contract.clientOrganisationId ? [contract.clientOrganisationId] : undefined).pipe(
                map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))),
              )}
              error={errors.clientUnit}
              required/>
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <InputLabel sx={{width: Sizes.drawerInputLabelWidth}}>
                {t('clients.details.contracts.manage.validity')}*
              </InputLabel>
              <Box flex={0.5}>
                <DatePickerComponent
                  label={t('global.dateFrom')}
                  placeholder={t('global.selectDate')}
                  handleChange={(startDate) =>
                    setContract((state) => ({
                      ...state,
                      startDate: dayjs(startDate).set('hour', 12).toDate(),
                    }))
                  }
                  error={errors.startDate}
                  value={contract.startDate}/>
              </Box>
              <Box flex={0.5}>
                <DatePickerComponent
                  label={t('global.dateTo')}
                  placeholder={t('global.selectDate')}
                  handleChange={(endDate) =>
                    setContract((state) => ({
                      ...state,
                      endDate: endDate ? dayjs(endDate).set('hour', 12).toDate() : undefined,
                    }))
                  }
                  error={errors.endDate}
                  value={contract.endDate}/>
              </Box>
            </Stack>
            {manageContract && <>
                <Stack spacing={2}>
                    <TextFieldComponent
                        labelWidth={Sizes.drawerInputLabelWidth}
                        label={t('clients.details.contracts.manage.reference')}
                        placeholder={t('global.writeSomething')}
                        value={contract.contractReference || ''}

                    />
                    <TextFieldComponent
                        labelWidth={Sizes.drawerInputLabelWidth}
                        label={t('clients.details.contractedServices.manage.idDatahub')}
                        placeholder={t('global.writeSomething')}
                        value={contract.datahubSpec?.idDatahub || ''}
                        handleChange={(idDatahub) => setContract({
                          ...contract,
                          datahubSpec: {
                            ...contract.datahubSpec,
                            idDatahub: idDatahub,
                          }
                        })}
                    />
                    <TextFieldComponent
                        labelWidth={Sizes.drawerInputLabelWidth}
                        label={t('clients.details.contractedServices.manage.additionalInformation')}
                        placeholder={t('global.writeSomething')}
                        value={contract.datahubSpec?.additionalInformation || ''}
                        handleChange={(ai) => {
                          setContract({
                            ...contract,
                            datahubSpec: {
                              ...contract.datahubSpec,
                              additionalInformation: ai,
                            }
                          });
                        }}
                    />
                </Stack>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                    <InputLabel sx={{width: Sizes.drawerInputLabelWidth}} required={false}>
                      {t('clients.details.contracts.manage.servicesLinked')}
                    </InputLabel>
                    <IconButton
                        onClick={() => setShowContractServices(manageContract?.id)}
                    >
                        <OpenInNewIcon color="primary"/>
                    </IconButton>
                </Stack>
            </>}
          </Stack>
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal"/>
        <Stack py="20px" spacing={2}>
          {contract.clientOrganisationId && <AsyncSelectComponent
              labelWidth={Sizes.drawerInputLabelWidth}
              label={t('unitDetails.contacts.address.internalName')}
              placeholder={t('global.writeSomething')}
              getOptions={() => clientDetailsService.getInvoiceAddresses(contract.clientOrganisationId || 0).pipe(
                map((options) => [{
                    label: 'PAR DEFAUT (Adresse Organisation)',
                    value: undefined
                  }, ...(options.map((o) => ({
                    label: o.addressCode + ' - ' + o.internalName || '-',
                    value: o.clientInvoiceAddressId || 0,
                    data: o,
                  })))]
                ))}
              value={contract.clientInvoiceAddressId ? {
                label: contract.invoiceAddress?.internalName || '',
                value: contract.clientInvoiceAddressId || 0
              } : null}
              handleChange={(value) => {
                updateContract({clientInvoiceAddressId: value.value, invoiceAddress: value?.data});
              }}
          />}
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street1')}
            placeholder={t('global.writeSomething')}
            value={address?.street || ''}
            readOnly
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street2')}
            placeholder={t('global.writeSomething')}
            value={address?.street2 || ''}
            readOnly
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street3')}
            placeholder={t('global.writeSomething')}
            value={address?.street3 || ''}
            readOnly
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.city')}
            placeholder={t('global.writeSomething')}
            value={address?.city || ''}
            readOnly
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.zip')}
            placeholder={t('global.writeSomething')}
            value={address?.zip || ''}
            readOnly/>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.country')}
            placeholder={t('global.writeSomething')}
            value={address?.country || ''}
            readOnly
          />
        </Stack>
      </Stack>
      <Stack alignSelf="flex-end" spacing={3} pb="25px" pt="10px" pr="10px">
        <Button
          variant="contained"
          color="primary"
          disabled={loading || checkIfErrors(errors)}
          onClick={handleSave}>
          {t('global.save')}
        </Button>
      </Stack>

      {!!showContractServices && (
        <ContractServicesModal
          contract={contract.originContract}
          contractId={showContractServices}
          handleClose={() => setShowContractServices(undefined)}/>
      )}
      {showWasteContainer && manageContract && (
        <UpdateWasteContainerModal contract={manageContract} handleClose={() => setShowWasteContainer(false)}/>)}
    </Drawer>
  );
};

export default ManageContractDrawer;
